import { useMemo } from 'react';
import {
  Database,
  Layers,
  Network,
  Clock,
  Zap,
  AlertCircle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Globe,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  ConnectionType,
  type ConnectionStatus,
} from '@/stores/useConnectionStatusStore';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const DB_META: Record<ConnectionType, { label: string; icon: typeof Database }> = {
  [ConnectionType.PostgreSQL]: { label: 'PostgreSQL', icon: Database },
  [ConnectionType.Neo4j]:      { label: 'Neo4j',      icon: Network },
  [ConnectionType.Qdrant]:     { label: 'Qdrant',     icon: Layers },
};

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 5) return 'gerade eben';
  if (seconds < 60) return `vor ${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `vor ${minutes}m`;
  return `vor ${Math.floor(minutes / 60)}h`;
}

/* ------------------------------------------------------------------ */
/*  Connection row                                                     */
/* ------------------------------------------------------------------ */

function ConnectionRow({ conn }: { conn: ConnectionStatus }) {
  const meta = DB_META[conn.type] ?? { label: conn.name, icon: Database };
  const Icon = meta.icon;

  return (
    <div className="flex flex-col gap-1.5 py-2">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">{meta.label}</span>
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
            conn.isConnected
              ? 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400'
              : 'bg-red-500/10 text-red-600 dark:bg-red-500/20 dark:text-red-400',
          )}
        >
          {conn.isConnected ? (
            <CheckCircle2 className="h-3 w-3" />
          ) : (
            <XCircle className="h-3 w-3" />
          )}
          {conn.isConnected ? 'Connected' : 'Disconnected'}
        </span>
      </div>

      {/* Detail row */}
      <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
        {conn.host && (
          <span className="inline-flex items-center gap-1">
            <Globe className="h-3 w-3" />
            {conn.host}
          </span>
        )}
        <span className="inline-flex items-center gap-1">
          <Zap className="h-3 w-3" />
          {conn.latencyMs}ms
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatRelativeTime(conn.checkedAt)}
        </span>
      </div>

      {/* Error */}
      {conn.error && (
        <div className="flex items-start gap-1.5 rounded bg-red-500/10 px-2 py-1 text-[11px] text-red-600 dark:text-red-400">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="break-all">{conn.error}</span>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Public component                                                   */
/* ------------------------------------------------------------------ */

interface ConnectionDetailPopoverProps {
  connections: ConnectionStatus[];
  checkedAt: string | null;
  isLoading: boolean;
  error: string | null;
  onRefresh?: () => void;
  children: React.ReactNode;
}

export function ConnectionDetailPopover({
  connections,
  checkedAt,
  isLoading,
  error,
  onRefresh,
  children,
}: ConnectionDetailPopoverProps) {
  const sortedConnections = useMemo(
    () => [...connections].sort((a, b) => a.type - b.type),
    [connections],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-80 p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-xs font-semibold">Database Connections</span>
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={isLoading}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              <RefreshCw className={cn('h-3 w-3', isLoading && 'animate-spin')} />
              Refresh
            </button>
          )}
        </div>

        {/* Connection list */}
        <div className="px-3">
          {error && (
            <div className="my-3 flex items-start gap-1.5 rounded bg-red-500/10 px-2 py-2 text-[11px] text-red-600 dark:text-red-400">
              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
              <span className="break-all">Status request failed: {error}</span>
            </div>
          )}
          {sortedConnections.length === 0 && (
            <p className="py-4 text-center text-xs text-muted-foreground">
              {error ? 'No status data available' : 'No connections configured'}
            </p>
          )}
          {sortedConnections.map((conn, idx) => (
            <div key={`${conn.type}-${conn.name}`}>
              <ConnectionRow conn={conn} />
              {idx < sortedConnections.length - 1 && <Separator />}
            </div>
          ))}
        </div>

        {/* Footer */}
        {checkedAt && (
          <div className="border-t px-3 py-1.5 text-[10px] text-muted-foreground">
            Last check: {formatRelativeTime(checkedAt)}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
